/* eslint-disable @typescript-eslint/no-explicit-any */
import { faFolder, faCoin, faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { Autocomplete, Tab, TextField } from "@mui/material";

import { format } from "date-fns";

import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { Transforms, BaseEditor, Descendant } from "slate";

import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";

import { Link } from "react-router-dom";

import { TabContext, TabList } from "@mui/lab";

import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";

import suggestionsData from "@/utils/suggestions";

import { Icon, Modal } from "@/components";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import PromptEditor from "@/pages/Private/pages/List/components/PromptEditor";
import { AiApplyType, CustomColumnTypes, PriorityFilterStatus, UpdateType } from "@/enum/list.enum";

import { TextArea } from "@/components/TextArea/TextArea";
import { InputField } from "@/components/InputField/InputField";

import { ButtonSize, ButtonColor } from "@/components/Button/types";
import { useAppSelector } from "@/redux/hooks";

import { Button } from "@/components/Button/Button";

import { ColumnConfig } from "@/pages/Private/pages/List/schema/columnConfig";

import { Toggle } from "@/components/Toggle/Toggle";

import { classNames } from "@/utils/classNames";

import { LiCompanyActivitiesInterface } from "@/pages/Private/pages/ProfileTracker/schema/li-company-activity-interface";

import { listSelector } from "@/pages/Private/redux/list/list.slice";

import {
	useRunAiPromptForListMutation,
	useRunLinkedinPostSearchForListMutation,
	useUpdateListColumnsMutation,
} from "@/pages/Private/redux/list/list.api";

import { CustomColumn, CustomColumnSchema, List } from "../../../schema/list";

import { usePersonSlider } from "../../../utils/usePersonSlider";
import { getSuggestions, replaceWithRealValues } from "../../../utils/column-utils";
import { getCurrentPrices } from "../../../utils/currentPrices";
import { CustomColumnApplyFilters } from "../customColumnApplyFilters";

export interface LinkedinPostModalProps {
	setShowConfirm: (value: boolean) => void;
	open: boolean;
	listId: number;
	setOpen: (value: boolean) => void;
	columnId?: number;
	column?: ColumnConfig;
	customColumnName?: string;
	filters?: CustomColumnApplyFilters;
	selectedProspectIds?: number[];
	list?: List;
	priorityFilterStatus?: PriorityFilterStatus[];
}

enum Tabs {
	POSTS = "POSTS",
	AI_PROCESSING = "AI_PROCESSING",
	SETTINGS = "SETTINGS",
}

export const LinkedinPostModal: FunctionComponent<LinkedinPostModalProps> = ({
	setShowConfirm,
	listId,
	open,
	setOpen,
	columnId,
	column,
	customColumnName,
	filters,
	selectedProspectIds,
	list,
	priorityFilterStatus,
}) => {
	const [applyTo, setApplyTo] = useState<AiApplyType>(AiApplyType.NONE);
	const [searchEditorValue, setSearchEditorValue] = useState<Descendant[]>();
	const [searchEditor, setSearchEditor] = useState<BaseEditor>();
	const [editorValue, setEditorValue] = useState<Descendant[]>();
	const [editor, setEditor] = useState<BaseEditor>();
	const [aiResult, setAiResult] = useState<{ answer: string; sourceUrl: string }>();
	const [searchResult, setSearchResult] = useState<LiCompanyActivitiesInterface>();

	const [currentTab, setCurrentTab] = useState<Tabs>(Tabs.POSTS);

	const { currentPerson, setCurrentPerson, renderPersonSlider } = usePersonSlider();

	const [selectedSearchVariable, setSelectedSearchVariable] = useState<
		{ id: string; name: string } | undefined
	>();
	const [selectedVariable, setSelectedVariable] = useState<
		{ id: string; name: string } | undefined
	>();

	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`aiColumn.${key}`), [t]);

	const [update, { isLoading, error }] = useUpdateListColumnsMutation();
	const [runPrompt, { isLoading: promptIsLoading }] = useRunAiPromptForListMutation();
	const [runSearch, { isLoading: searchIsLoading }] = useRunLinkedinPostSearchForListMutation();
	const { inbox, meta } = useAppSelector(listSelector);

	const tabs = [
		{ label: ts("tabs.posts"), value: Tabs.POSTS },
		{ label: `${ts("tabs.aiProcessing")} (optional)`, value: Tabs.AI_PROCESSING },
		{ label: ts("tabs.settings"), value: Tabs.SETTINGS },
	];

	const suggestions = useMemo(() => {
		return inbox[currentPerson] ? getSuggestions(inbox[currentPerson]) : [];
	}, [currentPerson, inbox]);

	const getSuggestionLabel = (key: string) => {
		return suggestionsData.suggestions[key as keyof typeof suggestionsData.suggestions] as string;
	};

	const handleRunSearch = async () => {
		const result = await runSearch({
			id: inbox[currentPerson].id,
			prompt: replaceWithRealValues(searchEditorValue, inbox[currentPerson], suggestions),
			online: true,
			prospectId: inbox[currentPerson].prospect?.id,
		}).unwrap();

		if (result?.data) {
			setSearchResult(result?.data);
		}
	};

	const {
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<CustomColumn>({
		defaultValues: {
			customColumnType: CustomColumnTypes.LINKEDIN_POST,
			online: false,
			...column,
			customColumnName: customColumnName || column?.name,
			value: column?.config,
		},
		resolver: zodResolver(CustomColumnSchema),
	});

	const handleRunPromptWithSearchResult = async () => {
		const result = await runPrompt({
			id: inbox[currentPerson].id,
			prompt: `Here ${getValues("single") ? "is" : "are"} the linkedin post(s): ${JSON.stringify(
				getValues("single")
					? searchResult?.posts?.[0].commentary
					: searchResult?.posts.map((post) => post.commentary)
			)}
			
			${replaceWithRealValues(editorValue, inbox[currentPerson], suggestions)} `,
			online: false,
			prospectId: inbox[currentPerson].prospect?.id,
		}).unwrap();

		setAiResult(result.data);
	};

	useEffect(() => {
		if (column) {
			setValue("customColumnName", column.name);
			setValue("value", column.config);
			setValue("sourceConfig", column.sourceConfig);

			setCurrentPerson(0);
		}
	}, [column]);

	const serialize = (nodes: any) => {
		return JSON.stringify(nodes);
	};

	const onSubmit = async (values: CustomColumn) => {
		try {
			values.value = editorValue ? serialize(editorValue) : "";
			values.sourceConfig = editorValue ? serialize(searchEditorValue) : "";

			const prospectIds =
				applyTo === AiApplyType.FIRST_10
					? inbox.slice(0, 10).map((p) => p.id)
					: applyTo === AiApplyType.CURRENTLY_SELECTED
						? selectedProspectIds
						: undefined;

			await update({
				...values,
				id: listId,
				updateType: UpdateType.CUSTOM_COLUMN,
				aiApplyType: applyTo,
				prospectIds: prospectIds,
				columnId,
				filters: applyTo === AiApplyType.CURRENTLY_FILTERED ? filters : undefined,
				priorityFilterStatus: priorityFilterStatus ?? undefined,
			}).unwrap();

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const handleTabsChange = useCallback((_: ChangeEvent<{}>, value: Tabs): void => {
		setCurrentTab(value);
	}, []);

	const applyToButtonText = useMemo(() => {
		return applyTo === AiApplyType.NONE
			? ts(`button.${AiApplyType.NONE}`)
			: applyTo === AiApplyType.FIRST_10
				? ts(`button.${AiApplyType.FIRST_10}`)
				: applyTo === AiApplyType.CURRENTLY_FILTERED
					? ts(`button.${AiApplyType.CURRENTLY_FILTERED}`)
					: ts(`button.${AiApplyType.ALL}`);
	}, [applyTo, ts]);

	const currentCreditCost = useMemo(() => {
		return getCurrentPrices({
			applyTo,
			meta,
			singlePrice: 1,
			selectedProspectIds,
			list,
		});
	}, [applyTo, getValues("online"), meta.itemCount, selectedProspectIds?.length]);

	return (
		<Modal
			handleClose={() => {
				setShowConfirm(true);
			}}
			handleSave={
				currentTab === Tabs.SETTINGS
					? handleSubmit(onSubmit)
					: () => setCurrentTab(currentTab === Tabs.POSTS ? Tabs.AI_PROCESSING : Tabs.SETTINGS)
			}
			isLoading={isLoading}
			isOpened={open}
			secondSubmitButtonText={t("basics.save")}
			size="lg"
			submitButtonText={currentTab === Tabs.SETTINGS ? applyToButtonText : "Next"}
			title="Create Linkedin Post Column"
		>
			<TabContext value={currentTab}>
				<TabList aria-label="Account tabs" sx={{ mb: 2.5 }} onChange={handleTabsChange}>
					{tabs.map((tab, index) => (
						<Tab
							key={index * 2}
							label={
								<div
									className={classNames(
										"flex leading-[28px]",
										currentTab === tab.value ? " px-7" : ""
									)}
								>
									{tab.label}
								</div>
							}
							sx={{ py: 1.25 }}
							value={tab.value}
						/>
					))}
				</TabList>

				<div className={classNames(Tabs.POSTS === currentTab ? "block" : "hidden")}>
					<div className="flex flex-col items-start">
						<div className="flex relative flex-row w-full items-start gap-4">
							<div className="w-2/3">
								<div className="text-ssm font-medium mt-2 mb-2">
									Search term for linkedin post search
								</div>
								<div className="flex relative flex-col border border-gray-300 rounded-xl">
									<div className="w-full min-h-[120px] ">
										<PromptEditor
											getSuggestionLabel={getSuggestionLabel}
											handleChange={(value) => {
												setSearchEditor(value);
											}}
											handleValueChange={(value) => {
												setSearchEditorValue(value);
											}}
											suggestions={suggestions}
											value={column?.sourceConfig ? JSON.parse(column?.sourceConfig) : undefined}
										/>
									</div>
									<div className="p-2 w-full flex border-t border-gray-300 justify-between items-center">
										<div className="flex items-center">
											<Autocomplete
												getOptionLabel={(option: { name: string; id: string }) => option.name}
												id="event"
												options={
													suggestions
														?.filter((key) => {
															const label = getSuggestionLabel(key);

															return label && label !== key;
														})
														?.map((key) => ({
															name: getSuggestionLabel(key) as string,
															id: key,
														})) || []
												}
												popupIcon={
													<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />
												}
												renderInput={(params) => {
													return <TextField {...params} placeholder="Variable" />;
												}}
												size="small"
												sx={{
													width: "220px",
													"& .MuiFilledInput-root": {
														paddingTop: "4px!important",
														paddingBottom: "4px!important",
														marginBottom: "0px!important",
													},
													"&.MuiFormControl-root": {
														height: "40px",
													},
												}}
												value={selectedSearchVariable}
												onChange={(event, value) => {
													if (value && searchEditor) {
														const mentionNode = {
															type: "mention" as const,
															character: `${value.id}`,
															children: [
																{
																	text: ``,
																},
															],
														};

														// Insert the mention node at the current selection
														Transforms.insertNodes(searchEditor as any, mentionNode);
														// Add a space after the mention
														Transforms.insertText(searchEditor as any, " ");

														setSelectedSearchVariable(undefined);
													}
												}}
											/>
											<div className="ml-2 text-sm text-gray-700">
												or enter{" "}
												<span className="bg-inactive-item px-1 border border-gray-200 rounded-md">
													{"{"}
												</span>{" "}
												to insert variable
											</div>
										</div>
										<div className="flex justify-end">
											<div className="max-w-[120px]">
												<Button
													isLoading={searchIsLoading}
													size={ButtonSize.S}
													title="Search for posts"
													onClick={handleRunSearch}
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="flex flex-col grow mr-2">
								<div className="text-ssm font-medium mb-2 mt-2">Search term preview</div>
								<TextArea
									className="!mb-0 grow mr-4 !min-h-[175px]"
									name={"randomValue"}
									placeholder="Enter search term on the left"
									showError={false}
									value={
										replaceWithRealValues(searchEditorValue, inbox[currentPerson], suggestions) ||
										""
									}
								/>
							</div>
						</div>
					</div>
					{searchResult?.posts && searchResult.posts.length > 0 && (
						<div className="flex flex-row">
							<div className="flex flex-col grow">
								<div className="text-ssm font-medium mt-4 mb-2">Prompt result preview</div>
								<div className="flex flex-col grow mr-2 max-h-[400px] overflow-y-auto  w-full">
									{searchResult?.posts.map((item, index) => (
										<div
											key={`items-linkedin-posts-${index * 2}`}
											className="flex flex-row items-start gap-4 px-4 py-4 rounded-xl border border-gray-200 mb-4 w-full"
										>
											<div className="flex flex-col items-start w-full">
												<div className="flex flex-row justify-between w-full">
													<div className="flex flex-row items-center mb-2">
														{item.actor?.actor_image && (
															<img
																alt="news"
																className="h-[40px] mr-2 rounded-full"
																referrerPolicy="no-referrer"
																src={
																	item.actor?.actor_image?.includes("http")
																		? item.actor.actor_image
																		: inbox[currentPerson].prospect?.image || ""
																}
															/>
														)}
														<div className="text-ssm">
															{item.actor.actor_navigationContext && (
																<Link to={item.actor.actor_navigationContext}>
																	{item.actor?.actor_name}
																</Link>
															)}
														</div>
													</div>
													<div className="text-ssm">
														{format(new Date(item.postedAt), "dd.MM.yyyy")}
													</div>
												</div>
												<div className="text-sm">{item.commentary}</div>
												<a
													className="text-sm cursor-pointer underline"
													href={item.share_url}
													rel="noreferrer"
													target="_blank"
												>
													Read more...
												</a>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					)}
					{renderPersonSlider(setAiResult, inbox)}
				</div>

				<div className={classNames(Tabs.AI_PROCESSING === currentTab ? "block" : "hidden")}>
					<div className="flex flex-col items-start">
						<div className="flex flex-row justify-between w-full">
							<div className="text-ssm font-medium mt-2 mb-1">Prompt editor</div>
							<div className="flex flex-row gap-2">
								<div className="text-ssm font-medium mt-2 mb-1 flex items-center">
									<Toggle
										isChecked={getValues("single") || false}
										onChange={() => {
											setValue("single", !getValues("single"), {
												shouldValidate: true,
												shouldDirty: true,
											});
										}}
									/>{" "}
									<span className="ml-2">Process each article separately</span>
								</div>
							</div>
						</div>
						<div className="flex relative flex-col w-full border border-gray-300 rounded-xl">
							<div className="w-full min-h-[120px] ">
								<PromptEditor
									getSuggestionLabel={getSuggestionLabel}
									handleChange={(value) => {
										setEditor(value);
									}}
									handleValueChange={(value) => {
										setEditorValue(value);
									}}
									suggestions={suggestions}
									value={column?.config ? JSON.parse(column?.config) : undefined}
								/>
							</div>
							<div className="p-2 w-full flex border-t border-gray-300 justify-between items-center">
								<div className="flex items-center">
									<div className="mr-2 text-sm text-gray-700">Select</div>
									<Autocomplete
										getOptionLabel={(option: { name: string; id: string }) => option.name}
										id="event"
										options={
											suggestions
												?.filter((key) => {
													const label = getSuggestionLabel(key);

													return label && label !== key;
												})
												?.map((key) => ({
													name: getSuggestionLabel(key) as string,
													id: key,
												})) || []
										}
										popupIcon={
											<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />
										}
										renderInput={(params) => {
											return <TextField {...params} placeholder="Variable" />;
										}}
										size="small"
										sx={{
											width: "220px",
											"& .MuiFilledInput-root": {
												paddingTop: "4px!important",
												paddingBottom: "4px!important",
												marginBottom: "0px!important",
											},
											"&.MuiFormControl-root": {
												height: "40px",
											},
										}}
										value={selectedVariable}
										onChange={(event, value) => {
											if (value && editor) {
												const mentionNode = {
													type: "mention" as const,
													character: `${value.id}`,
													children: [
														{
															text: ``,
														},
													],
												};

												// Insert the mention node at the current selection
												Transforms.insertNodes(editor as any, mentionNode);
												// Add a space after the mention
												Transforms.insertText(editor as any, " ");

												setSelectedVariable(undefined);
											}
										}}
									/>
									<div className="ml-2 text-sm text-gray-700">
										or enter{" "}
										<span className="bg-inactive-item px-1 border border-gray-200 rounded-md">
											{"{"}
										</span>{" "}
										to insert variable
									</div>
								</div>
								<div className="w-1/2 flex justify-end">
									<div className="w-1/2 mr-2 max-w-[150px]">
										<Button
											color={ButtonColor.DISABLED}
											disabled={true}
											image={<Icon className="mr-2" icon={faFolder} />}
											size={ButtonSize.S}
											title="Prompt library"
										/>
									</div>
									<div className="w-1/2 max-w-[110px]">
										<Button
											disabled={
												!replaceWithRealValues(editorValue, inbox[currentPerson], suggestions)
											}
											isLoading={promptIsLoading}
											size={ButtonSize.S}
											title="Test prompt"
											onClick={handleRunPromptWithSearchResult}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="flex flex-row">
						<div className="flex flex-col grow mr-2">
							<div className="text-ssm font-medium mt-4 mb-2">Prompt preview</div>
							<TextArea
								className="!mb-0 grow mr-4 max-h-[192px]"
								name={"randomValue"}
								placeholder="Enter value"
								showError={false}
								value={replaceWithRealValues(editorValue, inbox[currentPerson], suggestions) || ""}
							/>
						</div>
						<div className="flex flex-col grow ml-2">
							<div className="text-ssm font-medium mt-4 mb-2">Prompt result preview</div>
							<TextArea
								className="!mb-0 grow mr-4 max-h-[192px]"
								name={"randomValue"}
								showError={false}
								value={aiResult?.answer || ""}
							/>
						</div>
					</div>
					{renderPersonSlider(setAiResult, inbox)}
				</div>

				<div className={classNames(Tabs.SETTINGS === currentTab ? "block" : "hidden")}>
					<div className="text-ssm font-medium mb-2">
						<InputField
							error={!!errors.customColumnName}
							handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
								setValue("customColumnName", event.target.value, {
									shouldValidate: true,
									shouldDirty: true,
								});
							}}
							label="Column Name"
							name={"name"}
							placeholder="Enter column name"
							value={getValues("customColumnName")}
						/>
					</div>
					<div className="flex flex-row mt-5 border-t pt-5 border-gray-200">
						<Dropdown
							data={[
								{
									title: ts(AiApplyType.NONE),
									id: AiApplyType.NONE,
								},
								{
									title: ts(AiApplyType.FIRST_10),
									id: AiApplyType.FIRST_10,
								},
								{
									title: ts(AiApplyType.CURRENTLY_FILTERED),
									id: AiApplyType.CURRENTLY_FILTERED,
								},
								{
									title: ts(AiApplyType.CURRENTLY_SELECTED),
									id: AiApplyType.CURRENTLY_SELECTED,
								},
								{
									title: ts(AiApplyType.ALL),
									id: AiApplyType.ALL,
								},
							]}
							floating={false}
							handleSelect={function (value?: AutoCompleteItem) {
								if (value?.id) {
									setApplyTo(value.id as AiApplyType);
								}
							}}
							label="Apply to ..."
							value={applyTo ? { title: ts(applyTo), id: applyTo } : undefined}
						/>
					</div>
				</div>
			</TabContext>

			<div className="flex flex-row border-t pt-5 border-gray-200 text-gray-700 items-center text-sm mt-4">
				<span className="mr-1">1x</span>
				<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
				<span className="">per result</span>
			</div>
			{applyTo && currentCreditCost > 0 && (
				<div className="flex flex-row pt-5 text-gray-700 items-center text-sm">
					<span className="mr-1">{currentCreditCost}</span>
					<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
					<span className="">total</span>
				</div>
			)}

			{getAllErrors(error, formErrors).length ? (
				<div className="mt-4 mb-0">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
			) : null}
		</Modal>
	);
};
